import SearchMapOfferSkeleton from 'components/Search/SearchResultsMap/SearchMapOfferSkeleton'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButtonLoadingSkeleton from 'LuxKit/Button/TextButtonLoadingSkeleton'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import styled from 'styled-components'
import clsx from 'clsx'
import _SearchTileBase from './_SearchTileBase'
import _SearchTileContent from './_SearchTileContent'
import _SearchTileContentSecondary from './_SearchTileContentSecondary'
import _SearchTilePricing from './_SearchTilePricing'
import OfferDescriptionLoadingSkeleton from 'components/Common/Loading/OfferDescriptionLoadingSkeleton'
import { useAppSelector } from 'hooks/reduxHooks'
import getOfferListKey from 'lib/offer/offerListKey'
import { useShouldShowOfferDescription } from 'lib/offer/offerDescriptionUtils'
import type { SearchTileDensity, SearchTileTreatment } from './searchTileDensity'
import { COMPACT_SEARCH_TILE_IMAGE_COLUMN_WIDTH, COMPACT_SEARCH_TILE_MOSAIC_MIN_HEIGHT } from './searchTileLayoutStyles'

const Primary = styled(Group)`
  grid-area: content-primary;
`

const Hero = styled(LoadingBox)`
  grid-area: hero;

  &.density-cro-compact {
    width: ${rem(COMPACT_SEARCH_TILE_IMAGE_COLUMN_WIDTH)};
    min-height: ${rem(COMPACT_SEARCH_TILE_MOSAIC_MIN_HEIGHT)};
    border-radius: ${rem(8)};
  }
`

interface Props {
  filters?: App.OfferListFilters
  offerPosition?: number
  className?: string
  searchTileDensity?: SearchTileDensity
  searchTileTreatment?: SearchTileTreatment
}

function LoadingSearchTileSkeleton({
  filters,
  offerPosition,
  className,
  searchTileDensity = 'default',
  searchTileTreatment = 'default',
}: Props) {
  const descriptionLoading = useAppSelector((state) => state.offer.offerLists[filters ? getOfferListKey(filters) : '']?.descriptionLoading)
  const shouldShowDescription = useShouldShowOfferDescription(offerPosition, descriptionLoading)
  const offerDescBanner = shouldShowDescription ? <OfferDescriptionLoadingSkeleton /> : false
  const isCroCompact = searchTileDensity === 'cro-compact'
  const contentGap = isCroCompact ? 8 : 12

  if (searchTileDensity === 'map-row') return <SearchMapOfferSkeleton className={className} />

  return <_SearchTileBase
    banner={offerDescBanner}
    className={className}
    density={searchTileDensity}
    treatment={searchTileTreatment}
  >
    <Hero className={clsx({ 'density-cro-compact': isCroCompact })} />
    <_SearchTileContent density={searchTileDensity}>
      <Primary direction="vertical" gap={contentGap}>
        <VerticalSpacer gap={4}>
          <TextLoadingBox typography="body-small" width="20ch" />
          <TextLoadingBox typography="heading5" width="100%" lines={2} />
          {!isCroCompact && <TextLoadingBox typography="body-medium" width="80%" />}
          {isCroCompact && <TextLoadingBox typography="body-medium" width="70%" />}
          {isCroCompact && <TextLoadingBox typography="body-medium" width="55%" />}
        </VerticalSpacer>
      </Primary>
      <_SearchTileContentSecondary density={searchTileDensity}>
        <_SearchTilePricing
          action={!isCroCompact && <TextButtonLoadingSkeleton width="10ch" />}
          density={searchTileDensity}
        >
        </_SearchTilePricing>
      </_SearchTileContentSecondary>
    </_SearchTileContent>
  </_SearchTileBase>
}

export default LoadingSearchTileSkeleton
